"use client";

import { useEffect, type ReactNode } from "react";
import { usePathname } from "next/navigation";

// Per-session, in-memory scroll positions keyed by pathname.
const positions = new Map<string, number>();

/**
 * Wrapper that restores window scroll position per route.
 * The app scrolls on the window (not an inner overflow container), so we must
 * NOT introduce `overflow` here — doing so would create a sticky containing
 * block that never scrolls and would break `position: sticky` sidebars.
 * When returning to a page (e.g. back to /home from a store), the saved
 * position is restored once the async content has grown tall enough.
 */
export function ScrollArea({ children }: { children: ReactNode }) {
  const pathname = usePathname();

  // Continuously record the window scroll position for the current route.
  useEffect(() => {
    const onScroll = () => positions.set(pathname, window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [pathname]);

  // Restore the saved position on route change. Retry across frames so the
  // target is reached even while React Query content is still loading in.
  useEffect(() => {
    const target = positions.get(pathname) ?? 0;

    if (target === 0) {
      window.scrollTo(0, 0);
      return;
    }

    let frame = 0;
    let attempts = 0;
    const restore = () => {
      window.scrollTo(0, target);
      attempts += 1;
      if (attempts < 40 && Math.abs(window.scrollY - target) > 2) {
        frame = requestAnimationFrame(restore);
      }
    };
    frame = requestAnimationFrame(restore);
    return () => cancelAnimationFrame(frame);
  }, [pathname]);

  // `overflow-x-clip` clips horizontal overflow WITHOUT creating a scroll
  // container (unlike `overflow-x-hidden`, which would coerce overflow-y to
  // `auto` and break the sticky sidebars). `min-w-0` lets this flex child
  // shrink below its content width instead of pushing the row wider.
  return <div className="flex flex-1 flex-col min-w-0 overflow-x-clip">{children}</div>;
}
